<template>
  <div>
    <h5 class="day-title text-h5">
      {{ day.date }} ({{ day.orders.length }})
    </h5>

    <v-data-table
      :headers="headers"
      :items="day.orders"
      disable-pagination
      hide-default-footer
      class="order-list-table"
    >
      <!-- ID column -->
      <template v-slot:item.id="{ item }">
        {{ item.id }}
        <v-tooltip
          v-if="item.timestamps.last_edited_at"
          bottom
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="error"
              fab
              depressed
              x-small
              class="mini-alert-btn"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>
                mdi-exclamation-thick
              </v-icon>
            </v-btn>
          </template>
          <span>{{ getOrderLastEditedAtInfo(item) }}</span>
        </v-tooltip>
      </template>

      <!-- Status column -->
      <template v-slot:item.status="{ item }">
        <status-block
          :color="getColorByStatus(item.status)"
          :label="$t(`statuses.${item.status}`)"
        />
      </template>

      <!-- Address column -->
      <template v-slot:item.address="{ item }">
        <div class="address-info">
          <b>{{ item.customer.first_name }} {{ item.customer.last_name }}</b><br>
          <v-chip
            v-if="isClickAndCollectOrder(item)"
            class="cnc"
          >
            {{ $t('order.fulfillment_type.cnc') }}
          </v-chip>
          <div v-if="!isClickAndCollectOrder(item)">
            <div
              class="accent--text address-link"
              @click="showOnGoogleMaps(item.customer)"
            >
              <div>
                {{ item.customer.address }}
                <span v-if="item.customer.street_number"> {{ item.customer.street_number }}</span>
              </div>
              <div>
                {{ item.customer.postal_code }} {{ item.customer.city }} ({{ item.customer.province }})
              </div>
            </div>
            <div v-if="item.customer.doorbell">
              {{ item.customer.doorbell }}
            </div>
            <br>
            {{ $t('global.distance') }}: <b>{{ item.customer_to_store_distance }} km</b>
          </div>
        </div>
      </template>

      <!-- Delivery column -->
      <template v-slot:item.deliver_at_start="{ item }">
        <div class="deliver-slot">
          {{ getDeliverySlot(item.deliver_at_start, item.deliver_at_end) }}<br>
          <v-tooltip
            v-if="item.timestamps.delivery_moved_at"
            bottom
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="error"
                fab
                depressed
                x-small
                class="mini-alert-btn"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>
                  mdi-exclamation-thick
                </v-icon>
              </v-btn>
            </template>
            <span>{{ getOrderDeliverMovedAtInfo(item) }}</span>
          </v-tooltip>
        </div>
      </template>

      <!-- Total column -->
      <template v-slot:item.amount="{ item }">
        <div class="total text-right nowrap">
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
              >
                {{ item.payment_type === 'CREDIT_CARD' ? 'mdi-credit-card' : 'mdi-handshake' }}
              </v-icon>
            </template>
            <span>{{ item.payment_type === 'CREDIT_CARD' ? $t('order.payment.paid_with_credit_card') : $t('order.payment.cash_payment_required') }}</span>
          </v-tooltip>

          <div class="d-inline-flex">
            {{ item.amount_formatted }}
          </div>
        </div>
      </template>

      <!-- Items column -->
      <template v-slot:item.items="{ item }">
        <div class="nowrap">
          <div class="items-total">
            {{ $t('global.total') }}: {{ item.details_summary.total }}
          </div>
          <div>{{ $t('details_category.frozen') }}: {{ item.details_summary.frozen }}</div>
          <div>{{ $t('details_category.variable_weight') }}: {{ item.details_summary.variable_weight }}</div>
          <div>{{ $t('details_category.other') }}: {{ item.details_summary.other }}</div>
        </div>
      </template>

      <!-- Courier column -->
      <template v-slot:item.courier="{ item }">
        <div class="nowrap">
          <b>{{ $t('global.preparation') }}:</b> {{ item.picker_name }}<br>
          <div v-if="!isClickAndCollectOrder(item)">
            <b>{{ $t('global.delivery') }}:</b> {{ item.driver_name }}
          </div>
        </div>
      </template>

      <!-- Actions column -->
      <template v-slot:item.actions="{ item }">
        <v-btn
          color="accent"
          :to="`/orders/${item.id}`"
        >
          <v-icon left>
            mdi-pencil
          </v-icon>
          {{ $t('orders.manage') }}
        </v-btn>
      </template>
    </v-data-table>

    <!-- Day stats -->
    <v-row>
      <v-col>
        <StatusSummary :orders="day.orders" />
      </v-col>
      <v-col>
        <DetailsSummary :orders="day.orders" />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import DetailsSummary from './DetailsSummary'
import StatusSummary from './StatusSummary'
import StatusColor from './../Helpers/StatusColor'
import OrderHelper from './../Helpers/Order'
import StatusBlock from './../UI/StatusBlock'

export default {
  components: {
    DetailsSummary,
    StatusSummary,
    StatusBlock
  },

  props: {
    day: {
      type: Object,
      required: true
    }
  },

  computed: {
    headers () {
      return [
        { text: 'ID', value: 'id' },
        { text: this.$t('global.status'), value: 'status' },
        { text: this.$t('global.address'), value: 'address', sortable: false },
        { text: this.$t('global.delivery'), value: 'deliver_at_start', width: '120' },
        { text: this.$t('global.total'), value: 'amount', align: 'end' },
        { text: this.$t('global.items'), value: 'items', sortable: false },
        { text: this.$t('global.notes'), value: 'notes' },
        { text: this.$t('global.courier'), value: 'courier', sortable: false },
        { text: this.$t('global.actions'), value: 'actions', sortable: false }
      ]
    }
  },

  methods: {
    getColorByStatus: StatusColor.getColorByOrderStatus,
    getDeliverySlot: OrderHelper.getDeliverySlot,
    getOrderDeliverMovedAtInfo: OrderHelper.getOrderDeliverMovedAtInfo,
    getOrderLastEditedAtInfo: OrderHelper.getOrderLastEditedAtInfo,
    showOnGoogleMaps: OrderHelper.showOnGoogleMaps,
    isClickAndCollectOrder: OrderHelper.isClickAndCollectOrder,
  }
}
</script>

<style lang="scss">
.v-data-table .v-data-table__mobile-row {
  height: auto!important;
}

.order-list-table {
  tbody {
    vertical-align: top;

    td {
      padding-top: 5px!important;
      padding-bottom: 5px!important;
    }
  }

  .nowrap {
    white-space: nowrap;
  }
}

.mini-alert-btn {
  width: 16px!important;
  height: 16px!important;
  top: -1px;

  .v-icon {
    height: 10px!important;
    font-size: 12px!important;
    width: 10px!important;
  }
}
</style>

<style lang="scss" scoped>
.day-title {
  margin-bottom: 10px;
}

.status {
  color: #fff;
  font-weight: bold;
  text-transform: uppercase;
}

.address-info {
  max-width: 300px;
}

.address-link {
  text-decoration: underline;
  cursor: pointer;
}

.delivery-slot {
  min-width: 125px;
}

.total {
  font-size: 18px;
  font-weight: 500;
  vertical-align: top;

  div {
    margin-left: 5px;
    vertical-align: middle;
  }
}

.items-total {
  font-weight: 500;
  font-size: 18px;
}
</style>
